<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'ThematicPagesList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    EmptyMessage: () => import('@/components/general/EmptyMessage'),
    FilterList: () => import('@/components/general/FilterList'),
    Modal: () => import('@/components/general/Modal'),
    Pagination: () => import('@/components/general/Pagination'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Tabs: () => import('@/components/general/Tabs'),
    SelectField: () => import('@/components/general/SelectField'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        page: 1,
        limit: 10,
        lastPage: null,
        filter: {}
      },

      partnersListOptions: [],
      modalFiltersOpen: false,
      filteredFormLabels: {
        active: [
          {
            text: this.$t('solutions.manage:filtered-form.input.1.option.1'),
            value: 'true'
          },
          {
            text: this.$t('solutions.manage:filtered-form.input.1.option.2'),
            value: 'false'
          }
        ]
      },

      filteredForm: {
        partner_id: null,
        active: null
      },

      modalRemoveOpen: false,
      thematicPageId: null,

      tabLinks: [
        {
          location: { name: 'thematicPages.index' },
          text: this.$t('thematicPages:tabs.link.1')
        },
        {
          location: { name: 'pages.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ]
    }
  },

  computed: {
    ...mapState([ 'Auth' ]),

    thematicPagesList () {
      return this.$store.getters.thematicPagesList
    },

    canWrite () {
      return this.getContextPermission('thematic_pages') === 'write'
    },

    isPartnerUser () {
      return !!(this.Auth.userData && this.Auth.userData.partnerId)
    }
  },

  watch: {
    'pagination.page' () {
      this.getThematicPageList()
    }
  },

  created () {
    if (this.isPartnerUser) {
      this.pagination.filter.partner_id = this.Auth.userData.partnerId
    }

    this.getThematicPageList()

    if (!this.isPartnerUser) {
      this.attemptGetPartnersList({
        page: 1,
        limit: 200
      }).then((pagination) => {
        if (pagination.data) {
          this.partnersListOptions = [
            {
              text: this.$t('global:form.filter.all'),
              value: null
            },
            ...pagination.data
              .map((p) => ({
                text: p.name,
                value: p.id
              }))
              .sort((a, b) => a.text.localeCompare(b.text))
          ]
        }
      })
    }
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetThematicPagesList',
      'attemptRemoveThematicPage',
      'attemptToggleActiveThematicPage',
      'attemptGetPartnersList'
    ]),

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getThematicPageList () {
      this.setFetching(true)

      this.pagination.order = {
        active: 'desc',
        name: 'asc'
      }

      this.attemptGetThematicPagesList(this.pagination).then(pagination => {
        this.pagination.lastPage = pagination.lastPage
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getThematicPageList()
      } else {
        this.pagination.page = 1
      }
    },

    openFilterModal () {
      this.modalFiltersOpen = true
    },

    closeFilterModal () {
      this.modalFiltersOpen = false
    },

    applyFilter () {
      Object.keys(this.filteredForm).forEach(key => {
        if (this.filteredForm[key] === null || this.filteredForm[key] === undefined) {
          delete this.pagination.filter[key]
        } else {
          this.pagination.filter[key] = this.filteredForm[key]
        }
      })

      if (this.isPartnerUser) {
        this.pagination.filter.partner_id = this.Auth.userData.partnerId
      }

      this.closeFilterModal()

      if (this.pagination.page === 1) {
        this.getThematicPageList()
      } else {
        this.pagination.page = 1
      }
    },

    clearFilters () {
      Object.keys(this.filteredForm).forEach(key => {
        this.filteredForm[key] = null
        delete this.pagination.filter[key]
      })

      if (this.isPartnerUser) {
        this.pagination.filter.partner_id = this.Auth.userData.partnerId
      }

      this.closeFilterModal()

      if (this.pagination.page === 1) {
        this.getThematicPageList()
      } else {
        this.pagination.page = 1
      }
    },

    createNewThematicPage () {
      this.$router.push({ name: 'thematicPages.manage' })
    },

    searchThematicPage (search) {
      this.pagination.query = {
        name: search
      }

      if (this.pagination.page === 1) {
        this.getThematicPageList()
      } else {
        this.pagination.page = 1
      }
    },

    edit (thematicPageId) {
      this.$router.push({
        name: 'thematicPages.manage',
        params: { id: thematicPageId }
      })
    },

    remove () {
      this.modalRemoveOpen = false
      this.setFetching(true)

      this.attemptRemoveThematicPage(this.thematicPageId).then(() => {
        this.setFeedback({ message: this.$t('thematicPages:feedback.remove.success') })
        this.getThematicPageList()
      }).catch(() => {
        this.setFeedback({ message: this.$t('thematicPages:feedback.remove.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    confirmRemove (thematicPageId) {
      this.thematicPageId = thematicPageId
      this.modalRemoveOpen = true
    },

    cancelRemove () {
      this.modalRemoveOpen = false
    },

    openPage (track) {
      window.open(`${process.env.VUE_APP_PORTAL_URL}${track.thematicPageType.slug}/${track.slug}`, '_blank')
    },

    toggleActive (thematicPageId) {
      this.setFetching(true)

      this.attemptToggleActiveThematicPage(thematicPageId).then((response) => {
        this.setFeedback({
          message: response.data.active
            ? this.$t('thematicPages:feedback.toggle.activate.success')
            : this.$t('thematicPages:feedback.toggle.deactivate.success')
        })

        this.getThematicPageList()
      }).catch(() => {
        this.setFeedback({ message: this.$t('global.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('thematicPages:header.title')"
      :description="$t('thematicPages:header.description')"
      background="/assets/images/themes/default/png/partners_banner.png"
    >
      <Tabs
        v-if="canWrite"
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('thematicPages.list:btn.add')"
        @click="createNewThematicPage"
      />

      <Action
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('sessions.list:btn.filter')"
        @click="openFilterModal"
      />

      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_thematic_pages"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchThematicPage"
      />
    </FilterList>
    <div
      v-if="thematicPagesList.length > 0"
      class="center thematic-pages-table"
    >
      <Datatable :items="thematicPagesList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('thematicPages.list:datatable.header.1') }}
            </th>
            <th
              class="th"
              width="200"
            >
              {{ $t('thematicPages.list:datatable.header.2') }}
            </th>
            <th class="th">
              {{ $t('global:form.thematicPages.partner') }}
            </th>
            <th
              class="th"
              width="200"
            >
              {{ $t('thematicPages.list:datatable.header.3') }}
            </th>
            <th
              class="th"
              width="130"
            >
              {{ $t('global:datatable.audit.created-at') }}
            </th>
            <th
              class="th"
              width="180"
            >
              {{ $t('global:datatable.audit.updated-at') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>
            <td class="td">
              <span class="td-text bolder">{{ props.item.thematicPageType.name }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ props.item.partner ? props.item.partner.name : '—' }}</span>
            </td>
            <td class="td">
              <span
                v-if="!!props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ formatDate(props.item.createdAt) || '—' }}</span>
            </td>
            <td class="td">
              <span class="td-text td-block">{{ formatDate(props.item.userUpdatedAt || props.item.createdAt) || '—' }}</span>
              <span
                v-if="props.item.userUpdatedAt || props.item.createdAt"
                class="td-text td-audit-sub"
              >{{ formatTime(props.item.userUpdatedAt || props.item.createdAt) }}h{{ auditAuthorLabel(props.item) }}</span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  :text="$t('global:show.page')"
                  @click="openPage(props.item)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item.id, !props.item.active)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:remove')"
                  @click="confirmRemove(props.item.id, props.index)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center"
    >
      <EmptyMessage>
        <h2>{{ $t('thematicPages:list.empty.title') }}</h2>
        <p>{{ $t('thematicPages:list.empty.message') }}</p>
      </EmptyMessage>
    </div>
    <ModalConfirm
      :active="modalRemoveOpen"
      :title="$t('thematicPages.manage:remove.title')"
      :description="$t('thematicPages.manage:remove.description')"
      @confirmAction="remove"
      @cancelAction="cancelRemove"
    />
    <Modal
      class="modal-sessions-filters"
      :active="modalFiltersOpen"
      :cancel="true"
      @close="closeFilterModal"
    >
      <div class="modal-form modal-sessions-filters">
        <h2 class="modal-title">
          {{ $t('sessions.list:filter.title') }}
        </h2>
        <span class="modal-subtitle">{{ $t('sessions.list:filter.description') }}</span>
        <form @submit.prevent="applyFilter">
          <SelectField
            v-if="!isPartnerUser"
            v-model="filteredForm.partner_id"
            class="sessions-solutions-field"
            dark
            :label="$t('global:form.thematicPages.partner')"
            :items="partnersListOptions"
          />

          <SelectField
            v-model="filteredForm.active"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.1')"
            :items="filteredFormLabels.active"
          />

          <div class="form-buttons">
            <Action
              primary
              large
              dark
              fixed-width
              type="button"
              :text="$t('sessions.list:btn.clear.filter.action')"
              @click="clearFilters"
            />
            <Action
              secondary
              large
              dark
              submit
              fixed-width
              type="button"
              :text="$t('sessions.list:btn.filter.action')"
            />
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
.form-buttons {
  display: flex;
  justify-content: space-around;
}
</style>
